import type { IconName } from './vocabulary';
import coverageData from './coverage.json'
import { extendedRegistry } from './extend'
import { DEFAULT_FAMILY } from './families'
import { ICON_VOCABULARY } from './vocabulary'

const COVERAGE = coverageData as Record<string, string[]>

/**
 * Detailed coverage report for a per-theme iconography selection. The
 * `native` array lists vocabulary concepts the primary family can
 * resolve directly (identity hit OR machine-generated divergence
 * entry). `covered` lists concepts the primary lacks but the
 * secondary/fallback tier picks up. `uncovered` lists concepts that
 * fall through every tier and end up on the hard `lucide` fallback —
 * the designer probably wants to add an override for these.
 *
 * Extended vocabulary (consumer-registered concepts via
 * `extendVocabulary`) folds in automatically; their resolution is
 * driven by the per-entry `divergence` map.
 */
export interface CompatibilityReport {
  primary: string
  secondary?: string
  fallback?: string
  /** All vocabulary concepts considered, in declaration order. */
  total: IconName[]
  /** Concepts the primary family resolves natively. */
  native: IconName[]
  /** Concepts the primary lacks; covered by secondary or fallback. */
  covered: IconName[]
  /** Concepts unresolved by the chain; final hard fallback to lucide. */
  uncovered: IconName[]
  /** Per-concept resolution trace — useful for designer dialogs. */
  resolution: Record<string, {
    tier: 'primary' | 'secondary' | 'fallback' | 'hardcoded-lucide'
    family: string
  }>
}

/**
 * Walk a tier chain (primary → secondary → fallback → DEFAULT_FAMILY)
 * and return the first family that resolves `concept`. Returns
 * `DEFAULT_FAMILY` when nothing matches — caller decides whether that
 *'s an iconify placeholder situation or acceptable.
 *
 * Used by `Icon.vue` and `server.ts` to avoid silently rendering a
 * placeholder when the primary family lacks a concept that a later
 * tier covers. Pure (no I/O): driven entirely by build-time
 * coverage.json plus the in-memory extended registry.
 */
export function pickResolvingFamily(
  concept: string,
  primary: string,
  secondary?: string,
  fallback?: string,
): string {
  if (familyResolves(concept, primary)) return primary
  if (secondary && secondary !== primary && familyResolves(concept, secondary)) return secondary
  if (
    fallback
    && fallback !== primary
    && fallback !== secondary
    && familyResolves(concept, fallback)
  ) {
    return fallback
  }
  return DEFAULT_FAMILY
}

export function familyResolves(concept: string, family: string): boolean {
  // Extended vocabulary: trust the consumer's declared divergence map.
  // A consumer entry without divergence still maps via identity (the
  // concept name itself), so the existence of an entry implies "yes".
  const ext = extendedRegistry.value[concept]
  if (ext) {
    if (!ext.divergence) return true
    return family in ext.divergence || family === DEFAULT_FAMILY
  }
  // Built-in vocabulary: COVERAGE[concept] lists families the
  // generator was able to resolve at build time.
  return COVERAGE[concept]?.includes(family) ?? false
}

function allConcepts(): string[] {
  const builtIn = [...ICON_VOCABULARY] as string[]
  const extended = Object.keys(extendedRegistry.value)
  return Array.from(new Set([...builtIn, ...extended]))
}

/**
 * Compute the compatibility report for a given iconography chain.
 * Pure function — safe to call from setup() or computed().
 *
 * Resolution order matches `Icon.vue`'s runtime:
 *   primary → secondary → fallback → hardcoded lucide.
 *
 * Example:
 * ```ts
 * const report = getCompatibilityReport('solar', 'mdi', 'lucide')
 * report.native.length     // → 75
 * report.uncovered.length  // → 0  (lucide hard fallback catches everything)
 * ```
 */
export function getCompatibilityReport(
  primary: string,
  secondary?: string,
  fallback?: string,
): CompatibilityReport {
  const concepts = allConcepts()
  const native: string[] = []
  const covered: string[] = []
  const uncovered: string[] = []
  const resolution: CompatibilityReport['resolution'] = {}

  const tiers: Array<{ name: 'primary' | 'secondary' | 'fallback', family: string }> = []
  tiers.push({ name: 'primary', family: primary })
  if (secondary && secondary !== primary) tiers.push({ name: 'secondary', family: secondary })
  if (fallback && fallback !== primary && fallback !== secondary) {
    tiers.push({ name: 'fallback', family: fallback })
  }

  for (const concept of concepts) {
    let placed = false
    for (const tier of tiers) {
      if (familyResolves(concept, tier.family)) {
        resolution[concept] = { tier: tier.name, family: tier.family }
        if (tier.name === 'primary') native.push(concept)
        else covered.push(concept)
        placed = true
        break
      }
    }
    if (!placed) {
      // Final tier: hardcoded lucide guarantee (DEFAULT_FAMILY).
      const lucideHas = familyResolves(concept, DEFAULT_FAMILY)
      if (lucideHas) {
        resolution[concept] = { tier: 'hardcoded-lucide', family: DEFAULT_FAMILY }
        covered.push(concept)
      } else {
        // Concept truly missing everywhere — designer must add an
        // override or accept iconify's placeholder render.
        resolution[concept] = { tier: 'hardcoded-lucide', family: DEFAULT_FAMILY }
        uncovered.push(concept)
      }
    }
  }

  return {
    primary,
    secondary,
    fallback,
    total: concepts as IconName[],
    native: native as IconName[],
    covered: covered as IconName[],
    uncovered: uncovered as IconName[],
    resolution,
  }
}

/**
 * Lightweight metrics summary — for inline UI badges or telemetry.
 */
export function summarizeCompatibility(report: CompatibilityReport): {
  totalCount: number
  nativeCount: number
  coveredCount: number
  uncoveredCount: number
  nativePercent: number
} {
  const totalCount = report.total.length
  const nativeCount = report.native.length
  const coveredCount = report.covered.length
  const uncoveredCount = report.uncovered.length
  return {
    totalCount,
    nativeCount,
    coveredCount,
    uncoveredCount,
    nativePercent: totalCount > 0 ? Math.round((nativeCount / totalCount) * 100) : 0,
  }
}

/**
 * Raw coverage data export — concept → families that resolve natively.
 * Useful for static analysis or custom UIs that need the matrix
 * without going through `getCompatibilityReport`.
 */
export { COVERAGE }
